// frontend/src/pages/manufacturing/CustomerDetail.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  ShoppingCart as OrdersIcon,
  Visibility as ViewIcon,
  ContentCopy,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { manufacturingAPI } from '../../services/api';
import usePageTitle from '../../hooks/usePageTitle';

// Component to display consignment and tracking info
function ConsignmentTrackingCell({ consignments }) {
  const [expanded, setExpanded] = useState(false);
  
  if (!consignments || consignments.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled">
        -
      </Typography>
    );
  }

  const activeConsignments = consignments.filter(c => c.status !== 'deleted');
  
  if (activeConsignments.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled">
        -
      </Typography>
    );
  }

  const handleCopyTracking = (trackingNumber, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(trackingNumber);
    toast.success(`Copied: ${trackingNumber}`);
  };

  const handleCopyConsignment = (consignmentNumber, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(consignmentNumber);
    toast.success(`Copied: ${consignmentNumber}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'shipped': return 'success';
      case 'label_printed': return 'warning';
      case 'created': return 'info';
      default: return 'default';
    }
  };

  const allTrackingNumbers = activeConsignments.flatMap(c => c.tracking_numbers || []);
  const shippedConsignments = activeConsignments.filter(c => c.status === 'shipped');

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Chip
          size="small"
          label={`${activeConsignments.length} con.`}
          color={shippedConsignments.length === activeConsignments.length ? 'success' : 'primary'}
          variant="outlined"
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
        {allTrackingNumbers.length > 0 && (
          <Chip
            size="small"
            label={`${allTrackingNumbers.length} trk`}
            color="default"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
        {activeConsignments.length > 1 && (
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            sx={{ padding: 0.25 }}
          >
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        )}
      </Box>

      <Box sx={{ mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              fontFamily: 'monospace',
              fontWeight: 'bold',
              fontSize: '0.7rem',
              color: activeConsignments[0].status === 'shipped' ? 'success.main' : 'text.primary'
            }}
          >
            {activeConsignments[0].consignment_number}
          </Typography>
          <Tooltip title="Copy">
            <IconButton 
              size="small" 
              onClick={(e) => handleCopyConsignment(activeConsignments[0].consignment_number, e)}
              sx={{ padding: 0.25 }}
            >
              <ContentCopy sx={{ fontSize: 10 }} />
            </IconButton>
          </Tooltip>
          <Chip
            size="small"
            label={activeConsignments[0].status}
            color={getStatusColor(activeConsignments[0].status)}
            sx={{ height: 14, fontSize: '0.6rem' }}
          />
        </Box>
        
        {activeConsignments[0].tracking_numbers?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {activeConsignments[0].tracking_numbers.slice(0, 2).map((tn, idx) => (
              <Chip
                key={idx}
                size="small"
                label={tn}
                variant="outlined"
                onClick={(e) => handleCopyTracking(tn, e)}
                sx={{ 
                  height: 16, 
                  fontSize: '0.6rem',
                  fontFamily: 'monospace',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              />
            ))}
            {activeConsignments[0].tracking_numbers.length > 2 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                +{activeConsignments[0].tracking_numbers.length - 2}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Collapse in={expanded}>
        {activeConsignments.slice(1).map((consignment) => (
          <Box key={consignment.id} sx={{ mb: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  color: consignment.status === 'shipped' ? 'success.main' : 'text.primary'
                }}
              >
                {consignment.consignment_number}
              </Typography>
              <Tooltip title="Copy">
                <IconButton 
                  size="small" 
                  onClick={(e) => handleCopyConsignment(consignment.consignment_number, e)}
                  sx={{ padding: 0.25 }}
                >
                  <ContentCopy sx={{ fontSize: 10 }} />
                </IconButton>
              </Tooltip>
              <Chip
                size="small"
                label={consignment.status}
                color={getStatusColor(consignment.status)}
                sx={{ height: 14, fontSize: '0.6rem' }}
              />
            </Box>
            
            {consignment.tracking_numbers?.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {consignment.tracking_numbers.slice(0, 2).map((tn, tnIdx) => (
                  <Chip
                    key={tnIdx}
                    size="small"
                    label={tn}
                    variant="outlined"
                    onClick={(e) => handleCopyTracking(tn, e)}
                    sx={{ 
                      height: 16, 
                      fontSize: '0.6rem',
                      fontFamily: 'monospace',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  />
                ))}
                {consignment.tracking_numbers.length > 2 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                    +{consignment.tracking_numbers.length - 2}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ))}
      </Collapse>
    </Box>
  );
}

const CustomerDetail = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState('all');

  usePageTitle(customer ? `Customer: ${customer.name}` : null);

  // Fetch customer details
  const fetchCustomer = useCallback(async () => {
    setLoading(true);
    try {
      const response = await manufacturingAPI.customers.get(customerId);
      setCustomer(response.data);
    } catch (error) {
      console.error('Error fetching customer:', error);
      toast.error('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // Fetch customer orders
  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await manufacturingAPI.customers.orders(customerId, params);
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, [customerId, statusFilter]);

  useEffect(() => {
    fetchCustomer();
    fetchOrders();
  }, [fetchCustomer, fetchOrders]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Navigate to order detail
  const handleViewOrder = (orderId) => {
    navigate(`/manufacturing/customer-orders/${orderId}`);
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value) return '\u00A30.00';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(value);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      new: 'info',
      confirmed: 'primary',
      in_production: 'warning',
      ready_to_ship: 'success',
      partially_shipped: 'secondary',
      complete: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  // Filter and paginate orders
  const filteredOrders = orders.filter(order => {
    if (statusFilter === 'all') return true;
    return order.status === statusFilter;
  });

  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Customer not found</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/manufacturing/customers')} sx={{ mt: 2 }}>
          Back to Customers
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/manufacturing/customers')}
          variant="outlined"
        >
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight="bold">
            {customer.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            {customer.sage_customer_code && (
              <Chip label={`Sage: ${customer.sage_customer_code}`} size="small" variant="outlined" />
            )}
            <Chip 
              label={customer.is_active ? 'Active' : 'Inactive'}
              size="small"
              color={customer.is_active ? 'success' : 'default'}
            />
          </Box>
        </Box>
      </Box>

      {/* Customer Info Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Contact Info */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Contact Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {customer.primary_contact && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2">{customer.primary_contact}</Typography>
                  </Box>
                )}
                
                {customer.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2">{customer.email}</Typography>
                  </Box>
                )}
                
                {customer.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{customer.phone}</Typography>
                  </Box>
                )}
                
                {customer.phone_2 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{customer.phone_2}</Typography>
                  </Box>
                )}
                
                {!customer.primary_contact && !customer.email && !customer.phone && (
                  <Typography variant="body2" color="text.secondary">
                    No contact information on file
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Address */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Default Delivery Address
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {customer.default_delivery_address ? (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LocationIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {customer.default_delivery_address}
                    {customer.default_delivery_postcode && (
                      <>{'\n'}{customer.default_delivery_postcode}</>
                    )}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No delivery address on file
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Order Stats */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Order Statistics
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary" fontWeight="bold">
                      {customer.total_orders || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Orders
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main" fontWeight="bold">
                      {customer.outstanding_orders || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Outstanding
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', backgroundColor: 'grey.50', borderRadius: 1, p: 1.5, mt: 1 }}>
                    <Typography variant="h5" color="success.main" fontWeight="bold">
                      {formatCurrency(customer.total_order_value)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Order Value
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Notes */}
        {customer.notes && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Notes
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {customer.notes}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Orders Section */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OrdersIcon color="primary" />
            <Typography variant="h6">Orders</Typography>
            <Chip label={filteredOrders.length} size="small" color="primary" />
          </Box>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="in_production">In Production</MenuItem>
              <MenuItem value="ready_to_ship">Ready to Ship</MenuItem>
              <MenuItem value="partially_shipped">Partially Shipped</MenuItem>
              <MenuItem value="complete">Complete</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {ordersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredOrders.length === 0 ? (
          <Alert severity="info">
            No orders found {statusFilter !== 'all' && `with status "${statusFilter.replace('_', ' ')}"`}
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell><strong>Order Number</strong></TableCell>
                    <TableCell><strong>Customer Ref</strong></TableCell>
                    <TableCell><strong>Order Date</strong></TableCell>
                    <TableCell><strong>Due Date</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Items</strong></TableCell>
                    <TableCell align="right"><strong>Value</strong></TableCell>
                    <TableCell align="center"><strong>Progress</strong></TableCell>
                    <TableCell><strong>Consignment / Tracking</strong></TableCell>
                    <TableCell><strong>Dispatched</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedOrders.map((order) => (
                    <TableRow 
                      key={order.id}
                      hover
                      onClick={() => handleViewOrder(order.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="primary">
                          {order.order_number}
                        </Typography>
                        {order.sage_order_number && (
                          <Typography variant="caption" color="text.secondary">
                            Sage: {order.sage_order_number}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.customer_reference || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(order.order_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2"
                          color={order.is_overdue ? 'error' : 'text.primary'}
                          fontWeight={order.is_overdue ? 'bold' : 'normal'}
                        >
                          {formatDate(order.due_date)}
                        </Typography>
                        {order.days_until_due !== null && order.days_until_due !== undefined && (
                          <Typography variant="caption" color={order.days_until_due < 0 ? 'error' : 'text.secondary'}>
                            {order.days_until_due < 0 
                              ? `${Math.abs(order.days_until_due)} days overdue`
                              : order.days_until_due === 0 
                                ? 'Due today'
                                : `${order.days_until_due} days`
                            }
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={order.status?.replace('_', ' ')} 
                          size="small" 
                          color={getStatusColor(order.status)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {order.lines?.length || order.total_quantity_ordered || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(order.total_order_value)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {order.completion_percentage !== undefined && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box 
                              sx={{ 
                                width: 60, 
                                height: 6, 
                                backgroundColor: 'grey.200', 
                                borderRadius: 3,
                                overflow: 'hidden'
                              }}
                            >
                              <Box 
                                sx={{ 
                                  width: `${order.completion_percentage}%`, 
                                  height: '100%', 
                                  backgroundColor: order.completion_percentage === 100 ? 'success.main' : 'primary.main',
                                  borderRadius: 3
                                }} 
                              />
                            </Box>
                            <Typography variant="caption">
                              {Math.round(order.completion_percentage)}%
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <ConsignmentTrackingCell consignments={order.dx_consignments} />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const shippedConsignments = order.dx_consignments?.filter(c => c.shipped_at && c.status !== 'deleted') || [];
                          
                          if (shippedConsignments.length > 0) {
                            const latestShipped = shippedConsignments.reduce((latest, c) => 
                              new Date(c.shipped_at) > new Date(latest.shipped_at) ? c : latest
                            );
                            return (
                              <Box>
                                <Typography variant="body2" color="success.main" fontWeight="medium">
                                  {formatDate(latestShipped.shipped_at)}
                                </Typography>
                              </Box>
                            );
                          }
                          
                          if (order.completed_at) {
                            return (
                              <Box>
                                <Typography variant="body2">
                                  {formatDate(order.completed_at)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Completed
                                </Typography>
                              </Box>
                            );
                          }
                          
                          return (
                            <Typography variant="caption" color="text.disabled">
                              -
                            </Typography>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredOrders.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CustomerDetail;